import React, { useEffect, useState } from 'react';
import notify from '../../utils/notifier';
import LocationsService from '../../services/LocationsService';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PaginationComponent from './../PaginationComponent';
import DownloadIcon from '@mui/icons-material/Download';
import { IconButton } from '@mui/material';


const downloadStates = async (selectedCountry) => {
    let query = {_countryIso2 : selectedCountry};
    try {
      if(query._format === undefined || !query._format){
        query._format = 'CSV'
      }

      const response = await LocationsService.downloadStatesData(query);
      console.log('Download response:', response.data);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      
      const timestamp = new Date().toLocaleString('en-GB').replace(/[/,: ]/g, '-');
      const fileName = `states_${timestamp}.csv`;
      
      link.download = fileName; 
      link.click();
      setTimeout(() => {
        notify({
          type: 'success',
          message: 'File downloaded successfully.'
        });
      }, 50); 
    } catch (error) {
      console.error('Failed to download file:', error);
      notify({
        type: 'error',
        message: 'Something went wrong while downloading the file.'
      });
    }
};

const fetchStates = async (countryCode = 'IN') => {
  let output = [];
  try {
    const query = {_countryIso2 : countryCode};
    const response = await LocationsService.getStatesData(query);
    output.data = []
    response.data.forEach(response => {
      output.push(response)
    })
  } catch (error) {
    notify({
      type: 'error',
      message: 'Internal Server Error.'
    });
  }
  return output;
};

const fetchCountries = async () => {
  let output = [];
  try {
    const response = await LocationsService.getCountriesData();
    output.data = []
    response.data.forEach(response => {
      output.push(response)
    })
  } catch (error) {
    notify({
      type: 'error',
      message: 'Internal Server Error'
    });
  }
  return output;
};

const columns = [
  { id: 'id', label: 'ID' },
  { id: 'name', label: 'State Name' },
  { id: 'stateIso2', label: 'State ISO2' },
  { id: 'stateCode', label: 'State Code' },
  { id: 'countryName', label: 'Country Name' },
  { id: 'countryIso2', label: 'Country ISO2' },
];

const StaticStatesTable = () => {
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedCountry, setSelectedCountry] = useState('IN'); // Default to India
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCountries().then(setCountries);
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      setLoading(true);
      fetchStates(selectedCountry).then((data) => {
        setStates(data);
        setLoading(false);
      });
    }
  }, [selectedCountry]);

  useEffect(() => {
    setFiltered(
      states.filter(
        s =>
          (s.name && s.name.toLowerCase().includes(search.toLowerCase())) ||
          (s.stateIso2 && s.stateIso2.toLowerCase().includes(search.toLowerCase())) ||
          (s.stateCode && s.stateCode.toLowerCase().includes(search.toLowerCase())) || 
          (s.countryName && s.countryName.toLowerCase().includes(search.toLowerCase())) ||
          (s.countryIso2 && s.countryIso2.toLowerCase().includes(search.toLowerCase())) ||
          (s.id && s.id.toLowerCase().includes(search.toLowerCase()))
      )
    );
    setPage(0); // Reset to first page when searching
  }, [search, states]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
    setSearch(''); // Clear search when country changes
  };

  // Calculate paginated data
  const paginatedData = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3, backgroundColor: '#F2F2F2', minHeight: 'calc(100vh - 144px)' }}>
      <Paper sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#298BC1' }}>
              States
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Country</InputLabel>
              <Select
                value={selectedCountry}
                label="Country"
                onChange={handleCountryChange}
              >
                {countries.map((country) => (
                  <MenuItem key={country.iso2} value={country.iso2}>
                    {country.name} ({country.iso2})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                variant="outlined"
                placeholder="Search by Name, State Code, Country Code, ID"
                value={search}
                onChange={e => setSearch(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
                  onClick={() => downloadStates(selectedCountry)}   
                  sx={{
                    backgroundColor: '#298BC1',
                    color: 'white',
                    '&:hover': { backgroundColor: '#2477a9' },
                    borderRadius: '6px',
                    p: 1.2
                  }}
                >
                <DownloadIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <TableContainer 
          component={Paper} 
          sx={{ 
            boxShadow: 'none', 
            border: '1px solid #e0e0e0',
            maxHeight: 'calc(100vh - 300px)',
            overflow: 'auto'
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {columns.map(col => (
                  <TableCell 
                    key={col.id} 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#333',
                      borderBottom: '2px solid #e0e0e0',
                      backgroundColor: '#f5f5f5'
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: '#666' }}>
                    Loading states...
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {paginatedData.map(row => (
                    <TableRow 
                      key={row.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f8f9fa' },
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                      }}
                    >
                      {columns.map(col => (
                        <TableCell 
                          key={col.id}
                          sx={{ 
                            borderBottom: '1px solid #e0e0e0',
                            color: '#333'
                          }}
                        >
                          {row[col.id]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {paginatedData.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: '#666' }}>
                        No states found.
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2 }}>
          <PaginationComponent
            queryCount={filtered.length}
            page={page}
            handleChangePage={handleChangePage}
            rowsPerPage={rowsPerPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default StaticStatesTable; 